import { Loader2 } from "lucide-react"
import { useEffect, useState, type ReactNode } from "react"

import { NewPasswordCard } from "@/components/auth/sign-in-card"
import { SignInPage } from "@/components/auth/sign-in-page"
import { AmbientBackdrop } from "@/components/layout/hero"
import { SignedInContext, type SignedIn } from "@/hooks/use-signed-in"
import {
  loadAuthConfig,
  onUnauthenticated,
  signOut,
  startAuth,
  takeRecovery,
  type Session,
} from "@/lib/auth"

type GateState =
  | { kind: "loading" }
  | { kind: "unreachable"; message: string }
  | { kind: "open" }
  | { kind: "signed-out" }
  | { kind: "signed-in"; session: Session }
  | { kind: "recovery"; session: Session }

/**
 * Nothing in the application renders until this says so.
 *
 * It asks the server how sign-in works, then either shows the sign-in page or
 * the app. It also listens for two ways a session ends: Supabase reporting a
 * sign-out (or a refresh that failed), and the API answering 401 - the server
 * is the authority, so a token it refuses means signed out, whatever the page
 * believed.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: "loading" })

  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | undefined

    loadAuthConfig()
      .then(async (config) => {
        const client = startAuth(config)
        if (cancelled) return
        if (!client) {
          setState({ kind: "open" })
          return
        }
        const { data } = await client.auth.getSession()
        if (cancelled) return
        // A password-reset link signs the user in, but only to choose a new
        // password: until they have, the app stays behind this gate.
        const recovering = takeRecovery()
        if (data.session) {
          setState({ kind: recovering ? "recovery" : "signed-in", session: data.session })
        } else {
          setState({ kind: "signed-out" })
        }
        const { data: listener } = client.auth.onAuthStateChange((event, session) => {
          if (event === "PASSWORD_RECOVERY" && session) {
            setState({ kind: "recovery", session })
            return
          }
          setState((current) => {
            if (!session) return { kind: "signed-out" }
            // Token refreshes during recovery must not open the app early.
            return current.kind === "recovery" && event !== "USER_UPDATED"
              ? { kind: "recovery", session }
              : { kind: "signed-in", session }
          })
        })
        unsubscribe = () => listener.subscription.unsubscribe()
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            kind: "unreachable",
            message: "Could not reach the Praxa server. Is it running?",
          })
        }
      })

    // Several requests can fail with 401 at once - a page loads three or four
    // things in parallel - and one sign-out is enough.
    let signingOut = false
    const stopListening = onUnauthenticated(() => {
      if (signingOut) return
      signingOut = true
      void signOut().finally(() => {
        signingOut = false
        setState((current) => (current.kind === "open" ? current : { kind: "signed-out" }))
      })
    })

    return () => {
      cancelled = true
      unsubscribe?.()
      stopListening()
    }
  }, [])

  if (state.kind === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
        <AmbientBackdrop />
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
        <span className="sr-only">Loading</span>
      </div>
    )
  }

  if (state.kind === "unreachable") {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <AmbientBackdrop />
        <p className="max-w-sm text-center text-sm text-muted-foreground">{state.message}</p>
      </div>
    )
  }

  if (state.kind === "signed-out") return <SignInPage />

  if (state.kind === "recovery") {
    const session = state.session
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <AmbientBackdrop />
        <div className="w-full max-w-sm">
          <NewPasswordCard onDone={() => setState({ kind: "signed-in", session })} />
        </div>
      </div>
    )
  }

  const value: SignedIn | null =
    state.kind === "signed-in" ? { email: state.session.user.email ?? null, signOut } : null

  return <SignedInContext.Provider value={value}>{children}</SignedInContext.Provider>
}
